import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
  AccountBalance as BancoIcon,
  LockOutlined as LockOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
  PROGRAMA_CONTABILIDAD,
  PROGRAMA_BANCOS,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatusPill, fpInputSx } from 'components/financepro';
import ConciliacionBancaria, { PROGRAMA_CONCILIACION } from './ConciliacionBancaria';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const botonNavySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
};

const botonOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const cellEllipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const statusVariant = (s) => {
  const m = {
    Pendiente: 'warning',
    'En proceso': 'info',
    'Con diferencias': 'danger',
    Conciliada: 'success',
    Anulada: 'neutral',
  };
  return m[s] || 'neutral';
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ESTADOS_FILTRO = ['', 'Pendiente', 'En proceso', 'Con diferencias', 'Conciliada', 'Anulada'];

/**
 * Listado de conciliaciones bancarias + formulario para iniciar una nueva.
 * Desde aquí se puede entrar a cada conciliación haciendo clic.
 */
const ListadoConciliaciones = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () =>
      tienePermisosModulo(roleDetalles, PROGRAMA_CONCILIACION) ||
      tienePermisosModulo(roleDetalles, PROGRAMA_BANCOS) ||
      tienePermisosModulo(roleDetalles, PROGRAMA_CONTABILIDAD),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_CONCILIACION),
    [roleDetalles]
  );

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);

  // ─── Vista: 'listado' | { id, numero }
  const [vista, setVista] = useState('listado');

  // ─── Listado
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCuenta, setFiltroCuenta] = useState('');

  // ─── Formas de pago con conciliación
  const [formasPago, setFormasPago] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);

  // ─── Dialog nueva conciliación
  const [dialogoNueva, setDialogoNueva] = useState(false);
  const [nuevaForm, setNuevaForm] = useState({
    cuenta_banco: '',
    cuenta_contable: '',
    fecha_inicio: '',
    fecha_fin: '',
    balance_banco: '',
  });
  const [creando, setCreando] = useState(false);

  const credencialesBase = useCallback(
    () => ({
      usuario: usuarioLog || '',
      password: claveLog || '',
      id_empresa: selectedCompany,
    }),
    [usuarioLog, claveLog, selectedCompany]
  );

  // Cargar formas de pago que necesitan conciliación
  const cargarFormasPago = useCallback(async () => {
    if (!puedeOperar) return;
    setCargandoFormas(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_listarformasdepago`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credencialesBase()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error');
      setFormasPago(data.formas_pago || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCargandoFormas(false);
    }
  }, [puedeOperar, servidor, credencialesBase]);

  // Cargar listado
  const cargarListado = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setFilas([]);
      return;
    }
    setCargando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_listarconciliaciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          filtro_status: filtroStatus,
          filtro_cuenta: filtroCuenta,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al cargar conciliaciones');
      setFilas(data.conciliaciones || []);
    } catch (e) {
      showError(e.message);
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, puedeOperar, servidor, credencialesBase, filtroStatus, filtroCuenta, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) {
      cargarListado();
      cargarFormasPago();
    }
  }, [puedeLeer, puedeOperar, cargarListado, cargarFormasPago]);

  const abrirNueva = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear conciliaciones');
      return;
    }
    setNuevaForm({
      cuenta_banco: '',
      cuenta_contable: '',
      fecha_inicio: '',
      fecha_fin: '',
      balance_banco: '',
    });
    setDialogoNueva(true);
  };

  const crearConciliacion = async () => {
    if (!puedeModificar) return;
    const { cuenta_banco, cuenta_contable, fecha_inicio, fecha_fin, balance_banco } = nuevaForm;
    if (!cuenta_banco) { showError('Seleccione una cuenta de banco'); return; }
    if (!fecha_inicio) { showError('La fecha de inicio es requerida'); return; }
    if (!fecha_fin) { showError('La fecha fin es requerida'); return; }

    setCreando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_crear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          cuenta_banco,
          cuenta_contable,
          fecha_inicio,
          fecha_fin,
          balance_banco: parseFloat(balance_banco) || 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al crear conciliación');
      showSuccess(`Conciliación #${data.numero} creada`);
      setDialogoNueva(false);
      // Ir directo a la conciliación creada
      setVista({ id: data.id, numero: data.numero });
    } catch (e) {
      showError(e.message);
    } finally {
      setCreando(false);
    }
  };

  const entrarConciliacion = (row) => {
    setVista({ id: row.id, numero: row.numero });
  };

  // Si está en detalle
  if (vista !== 'listado') {
    return (
      <ConciliacionBancaria
        conciliacionId={vista.id}
        onVolver={() => {
          setVista('listado');
          cargarListado();
        }}
      />
    );
  }

  // Acceso denegado
  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Conciliación Bancaria" subtitle="Módulo de Bancos" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder a la conciliación bancaria.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Conciliación Bancaria"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Conciliación de cuentas bancarias`
            : 'Conciliación de cuentas bancarias'
        }
      />

      <Card sx={{ ...cardShellSx, mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {/* Encabezado del listado */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.4,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BancoIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1rem' }}>
                Conciliaciones bancarias
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                Gestione las conciliaciones de sus cuentas bancarias. Haga clic en una fila para entrar.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={cargarListado}
                disabled={!puedeOperar || cargando}
                sx={botonOutlinedSx}
                size="small"
              >
                Actualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={abrirNueva}
                disabled={!puedeOperar || !puedeModificar}
                sx={botonNavySx}
                size="small"
              >
                Nueva conciliación
              </Button>
            </Box>
          </Box>

          {!puedeOperar && (
            <Box
              sx={{
                p: 1.4,
                mb: 2,
                borderRadius: 1.4,
                bgcolor: FP.color.infoSoft,
                color: FP.color.info,
                border: `1px solid ${FP.color.info}33`,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Seleccione una empresa con base de datos y asegúrese de tener sesión iniciada.
            </Box>
          )}

          {/* Filtros */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180, ...fpInputSx }}>
              <InputLabel shrink>Filtrar por status</InputLabel>
              <Select
                notched
                label="Filtrar por status"
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {ESTADOS_FILTRO.filter((e) => e).map((e) => (
                  <MenuItem key={e} value={e}>
                    {e}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200, ...fpInputSx }}>
              <InputLabel shrink>Filtrar por cuenta</InputLabel>
              <Select
                notched
                label="Filtrar por cuenta"
                value={filtroCuenta}
                onChange={(e) => setFiltroCuenta(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {formasPago.map((fp) => (
                  <MenuItem key={fp.id} value={fp.documento}>
                    {fp.documento}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              onClick={() => {
                setFiltroStatus('');
                setFiltroCuenta('');
              }}
              sx={{ textTransform: 'none', color: FP.color.textSecondary }}
            >
              Limpiar filtros
            </Button>
          </Box>

          {/* Tabla */}
          {cargando && filas.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
            </Box>
          ) : filas.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              <BancoIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
              <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                No hay conciliaciones registradas
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                Cree una con &quot;Nueva conciliación&quot;.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: 'calc(100vh - 380px)',
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 980, sm: 1180 } }}>
                <colgroup>
                  {['6%', '15%', '13%', '11%', '11%', '11%', '11%', '12%', '10%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        #
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Cuenta banco
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Período
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Saldo anterior
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Balance Libro
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Balance Banco
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Diferencia
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Acción
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {filas.map((r) => {
                    const periodo = `${r.fecha_inicio || ''} — ${r.fecha_fin || ''}`;
                    return (
                      <TableRow
                        key={r.id}
                        hover
                        onClick={() => entrarConciliacion(r)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{r.numero}</TableCell>
                        <TableCell sx={cellEllipsisSx} title={r.cuenta_banco || '—'}>
                          {r.cuenta_banco || '—'}
                        </TableCell>
                        <TableCell sx={cellEllipsisSx} title={periodo}>
                          {periodo}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {fmtMoney(r.balance_inicial)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {fmtMoney(r.balance_libro)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {fmtMoney(r.balance_banco)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: Math.abs(Number(r.diferencia || 0)) < 0.01 ? FP.color.success : FP.color.danger,
                          }}
                        >
                          {fmtMoney(r.diferencia)}
                        </TableCell>
                        <TableCell align="center">
                          <StatusPill label={r.status} variant={statusVariant(r.status)} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Abrir conciliación">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                entrarConciliacion(r);
                              }}
                              sx={{ color: FP.color.accentBlue, '&:hover': { bgcolor: FP.color.accentBlueSoft } }}
                            >
                              <OpenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog nueva conciliación */}
      <Dialog
        open={dialogoNueva}
        onClose={() => !creando && setDialogoNueva(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BancoIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Nueva conciliación bancaria
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogoNueva(false)} size="small" disabled={creando} sx={{ color: FP.color.textSecondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, md: 3 },
            pb: { xs: 2, md: 3 },
            pt: { xs: '20px !important', md: '24px !important' },
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
          }}
        >
          <FormControl fullWidth size="small" sx={fpInputSx}>
            <InputLabel shrink>Cuenta de banco *</InputLabel>
            <Select
              notched
              label="Cuenta de banco *"
              value={nuevaForm.cuenta_banco}
              onChange={(e) => {
                const sel = formasPago.find((fp) => fp.documento === e.target.value);
                setNuevaForm((p) => ({
                  ...p,
                  cuenta_banco: e.target.value,
                  cuenta_contable: sel?.cuenta_contable || '',
                }));
              }}
            >
              <MenuItem value="">— Seleccione —</MenuItem>
              {formasPago.map((fp) => (
                <MenuItem key={fp.id} value={fp.documento}>
                  {fp.documento} {fp.cuenta_contable ? `(${fp.cuenta_contable})` : ''}
                </MenuItem>
              ))}
            </Select>
            <Typography sx={{ mt: 0.5, color: FP.color.textSecondary, fontSize: '0.76rem' }}>
              Solo se muestran formas de pago configuradas para conciliación.
            </Typography>
          </FormControl>

          {nuevaForm.cuenta_contable && (
            <TextField
              fullWidth
              size="small"
              label="Cuenta contable"
              value={nuevaForm.cuenta_contable}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              required
              fullWidth
              size="small"
              label="Fecha inicio"
              type="date"
              value={nuevaForm.fecha_inicio}
              onChange={(e) => setNuevaForm((p) => ({ ...p, fecha_inicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
            <TextField
              required
              fullWidth
              size="small"
              label="Fecha fin"
              type="date"
              value={nuevaForm.fecha_fin}
              onChange={(e) => setNuevaForm((p) => ({ ...p, fecha_fin: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Balance según estado de cuenta del banco"
            type="number"
            value={nuevaForm.balance_banco}
            onChange={(e) => setNuevaForm((p) => ({ ...p, balance_banco: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            helperText="Ingrese el saldo final según el estado de cuenta bancario."
            sx={fpInputSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={() => setDialogoNueva(false)}
            startIcon={<CloseIcon />}
            disabled={creando}
            sx={{ textTransform: 'none', fontWeight: 700, color: FP.color.textSecondary, borderRadius: 1.4 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={creando ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={crearConciliacion}
            disabled={creando || !puedeModificar || !puedeOperar}
            sx={botonNavySx}
          >
            {creando ? 'Creando…' : 'Crear conciliación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListadoConciliaciones;
